/* eslint-disable import/exports-last */
import { Store } from '@ngrx/store';
import { Observable, combineLatest, filter, map, switchMap } from 'rxjs';

import { RecommendationsByPlacementParamsGroup, Placement, RecommendationsByPlacementParams, Strategy } from '../../domain/models/recommendations-by-placement.model';
import { ProductUid } from '../../domain/models/related-products.models';

import { CartItem } from '@api/cart/cart.models';
import { getLanguageLocale, getStoreId } from '@core/infrastructure/state/core.selectors';
import { ProductPublicModels } from '@features/products/product';
import { UserIdentity } from '@features/user';
import { ProductViewPlace } from '@shared/product/models';
import {
    RelatedProductsRepositoryInterface,
} from '@shared/product/related-products/domain/related-products-repository.interface';
import { getUserIdentity } from '@shared/user/store';

const getProductIds = (baseProducts: Array<ProductPublicModels.PublicProduct | CartItem>): string[] => {
    return baseProducts.filter(element => !!element).map(p => {
        if ('status' in p) return p.status?.selectedColor?.catentryId?.toString() || p.id;
        return p.origin?.[0]?.productId?.toString() || '';
    }).filter(id => !!id);
};

const getVisitorId = (googleAnalyticsCookie: string): string => {
    const gaSplit = googleAnalyticsCookie?.split('.');
    return gaSplit ? `${gaSplit[gaSplit.length - 2]}.${gaSplit[gaSplit.length - 1]}` : undefined;
};

const getSectionName: Record<number, string> = {
    1: 'WOMAN',
    2: 'MAN',
};

const getPlacement: Partial<Record<ProductViewPlace, Placement>> = {
    ADD_CART: 'cart_toast',
    CS_BS: 'cart_toast',
    OUT_OF_STOCK: 'home',
    PRODUCT_GRID: 'grid_page',
    PRODUCT_DETAIL: 'product_page',
    BUNDLE_DETAIL: 'product_page',
    CART: 'cart_page',
    WISHLIST: 'cart_page',
    SEARCH: 'search_page',
    ERROR: 'home',
};

const getStrategy: Record<string, Strategy> = {
    similares_yoda: 'SIMILAR',
    frecuently_bought_together: 'FBT',
    other_you_may_like: 'OYML',
    recommended_for_you: 'RFY',
    top_sales: 'TRENDS',
};

const isCart = (placeName: ProductViewPlace): boolean => placeName === 'CART';

export const getCampaign = (
    placeName: ProductViewPlace,
    campaign: string,
    products: Array<ProductPublicModels.PublicProduct | CartItem>,
): string => {
    if (!isCart(placeName)) {
        return campaign || products?.[0]?.reference?.split('-')?.[1];
    }

    return campaign;
};

const getRecommendationsByPlacementParams = (
    store: Store,
    params: RecommendationsByPlacementParamsGroup,
): Observable<RecommendationsByPlacementParams> => {
    return combineLatest([
        store.select(getStoreId).pipe(filter(val => !!val)),
        store.select(getLanguageLocale).pipe(filter(val => !!val)),
        store.select(getUserIdentity).pipe(filter(val => !!val)),
    ]).pipe(
        map(([storeId, locale, userIdentity]: [string, string, UserIdentity]) => ({
            storeId: storeId,
            locale,
            campaign: getCampaign(params.placeName, params.campaign, params.baseProducts),
            productIds: getProductIds(params.baseProducts),
            sectionName: getSectionName[params.section],
            placement: getPlacement[params.placeName],
            strategy: getStrategy[params.type],
            userId: userIdentity?.userId,
            categoryId: params.categoryId,
            type: params.type,
            sessionId: params.sessionId,
            limit: params.limit,
            visitorId: getVisitorId(params.googleAnalyticsCookie),
        })),
    );
};

export const getRecommendationsByPlacement = (
    service: RelatedProductsRepositoryInterface,
    store: Store,
    paramsGroup: RecommendationsByPlacementParamsGroup,
): Observable<ProductUid[]> => {
    return getRecommendationsByPlacementParams(
        store,
        paramsGroup,
    ).pipe(
        switchMap(params => service.getRecommendationsByPlacement$(params)),
    );
};
